import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Check, AlertCircle, Clock, Heart, Shield, Sparkles } from "lucide-react";
import { EditableText as T } from "@/components/live/EditableText";
import { EditableList } from "@/components/live/EditableList";
import { useContent } from "@/lib/content";
import { Section } from "@/components/live/Section";

const EXPECTATION_ICONS = [Shield, Heart, Clock, Sparkles];

const BookingEtiquette = () => {
  const { editMode } = useContent();
  const expectations = [
    {
      icon: Shield,
      title: "Discretion is Paramount",
      description: "Your privacy and mine are of the utmost importance. All interactions are held in the strictest confidence."
    },
    {
      icon: Heart,
      title: "Mutual Respect",
      description: "I seek connections with gentlemen who understand that respect is the foundation of any meaningful encounter."
    },
    {
      icon: Clock,
      title: "Punctuality & Communication",
      description: "Please arrive on time and communicate any changes promptly. I extend the same courtesy to you."
    },
    {
      icon: Sparkles,
      title: "Presentation Matters",
      description: "I take pride in my appearance; I appreciate the same attention to grooming and attire from my companions."
    }
  ];

  const donationGuidelines = [
    {
      duration: "Casual Encounter",
      description: "For gentlemen that prefer a simple, but effective arrangements. This is the perfect balance between small talk and play time.",
      note: "1-2 Hours"
    },
    {
      duration: "Dinner Date",
      description: "An evening of getting acquainted over an exquisite meal, followed by a private rendezvous that is sure to satisfy your sweet tooth.",
      note: "3 Hours"
    },
    {
      duration: "Extended Evening",
      description: "A full evening together, perfect for events, intimate dinners, or unhurried quality time.",
      note: "4-6 Hours"
    },
    {
      duration: "Overnight",
      description: "An evening extending into the morning, ideal for those seeking deeper connection.",
      note: "8-12 Hours"
    },
    {
      duration: "Arm Candy",
      description: "Perfect for those seeking a +1 for any public occasion, whether it be a corporate dinner/party, sporting event, or you're simply seeking someone to share conversation with over a nice meal-- I would be delighted to accompany.",
      note: "Custom Arrangements"
    },
    {
      duration: "Porn Star Experience",
      description: "The ultimate VIP experience, live out your wildest fantasies that most only see on screens.",
      note: "Custom Arrangements",
      specialNote: "Note: all PSE arrangements require a full urine and blood Health/STD screening within 3 days of the discussed arrangement date. Results MUST be available via an online portal (TTS, CLEAR, MyChart, etc.), paper/PDF results will NOT be accepted- no exceptions."
    }
  ];

  const requirements = [
    "Complete the inquiry form with accurate information",
    "Provide verification references (preferred) or alternative screening",
    "Allow 24-48 hours for inquiry response",
    "Confirm arrangements 24 hours in advance",
    "Donations are provided discreetly at the start of our time together"
  ];

  const notAccepted = [
    "Last-minute requests without proper screening",
    "Negotiation of donation amounts",
    "Explicit discussions via text or email",
    "Disrespectful or demanding behavior",
    "Cancellations without reasonable notice"
  ];

  return (
    <div data-testid="booking-etiquette-page">
      {/* Header */}
      <section className="page-header" data-testid="etiquette-header">
        <div className="max-w-4xl mx-auto px-6">
          <T k="etiquette.before-we-meet" as="p" className="font-body text-sm tracking-widest uppercase text-champagne-gold mb-4 animate-fade-in">Before We Meet</T>
          <T k="etiquette.booking-etiquette" as="h1" className="page-title animate-fade-in stagger-1">Booking Etiquette</T>
          <T k="etiquette.please-review-these" as="p" className="page-subtitle animate-fade-in stagger-2">Please review these guidelines to ensure our time together begins on the best possible note.</T>
        </div>
      </section>

      {/* Introduction */}
      <Section k="etiquette.section.etiquette-intro" label="etiquette intro" className="py-20 px-6 md:px-12 lg:px-24" data-testid="etiquette-intro">
        <div className="max-w-3xl mx-auto text-center">
          <T k="etiquette.i-believe-that" as="p" multiline className="font-body text-lg text-stone-grey leading-relaxed">I believe that the most memorable connections are built on a foundation of mutual understanding and respect. These guidelines are designed to create a comfortable experience for both of us, ensuring that our time together is nothing short of exceptional.</T>
        </div>
      </Section>

      {/* Expectations */}
      <Section k="etiquette.section.expectations-section" label="expectations" className="py-20 px-6 md:px-12 lg:px-24 bg-blush-pink/20" data-testid="expectations-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <T k="etiquette.what-i-value" as="p" className="font-body text-sm tracking-widest uppercase text-champagne-gold mb-4">What I Value</T>
            <T k="etiquette.my-expectations" as="h2" className="font-heading text-4xl text-charcoal">My Expectations</T>
          </div>

          <EditableList k="etiquette.expectations" items={expectations.map(({ title, description }) => ({ title, description }))} newItem={{ title: "New expectation", description: "Describe it here." }} addLabel="Add expectation" className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {(item, index, Field) => {
              const Icon = EXPECTATION_ICONS[index % EXPECTATION_ICONS.length];
              return (
                <div className="glass rounded-xl p-8 card-hover" data-testid={`expectation-${index}`}>
                  <Icon className="w-8 h-8 text-jade-text mb-4" />
                  <Field field="title" as="h3" className="font-heading text-xl text-charcoal mb-3" />
                  <Field field="description" as="p" multiline className="font-body text-stone-grey leading-relaxed" />
                </div>
              );
            }}
          </EditableList>
        </div>
      </Section>

      {/* Donation Guidelines */}
      <Section k="etiquette.section.donation-section" label="donation" className="py-20 px-6 md:px-12 lg:px-24" data-testid="donation-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <T k="etiquette.investment-in-our" as="p" className="font-body text-sm tracking-widest uppercase text-champagne-gold mb-4">Investment in Our Time</T>
            <T k="etiquette.arrangement-types" as="h2" className="font-heading text-4xl text-charcoal mb-6">Arrangement Types</T>
          </div>

          <EditableList k="etiquette.arrangements" items={donationGuidelines.map((g) => ({ ...g, specialNote: g.specialNote || "" }))} newItem={{ duration: "New arrangement", note: "Note", description: "Describe it here.", specialNote: "" }} addLabel="Add arrangement type" className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {(item, index, Field) => (
              <div className="glass rounded-xl p-8" data-testid={`donation-type-${index}`}>
                <div className="flex justify-between items-start mb-4">
                  <Field field="duration" as="h3" className="font-heading text-xl text-charcoal" />
                  <Field field="note" className="text-xs font-body tracking-widest uppercase text-jade-text bg-jade-soft/30 px-3 py-1 rounded-full" />
                </div>
                <Field field="description" as="p" multiline className="font-body text-stone-grey leading-relaxed" />
                {(item.specialNote || editMode) && (
                  <Field field="specialNote" as="p" multiline className="font-body text-xs text-lipstick-red mt-4 p-3 bg-lipstick-red/10 rounded-lg" />
                )}
              </div>
            )}
          </EditableList>

          <div className="mt-12 glass rounded-xl p-8 text-center">
            <T k="etiquette.donation-note" as="p" multiline className="font-body text-stone-grey">Note: For specific donation amounts, please submit an inquiry. Information is only shared with verified, serious inquiries. (Excluding travel arrangements, please see "Travel" section for these details.)</T>
          </div>
        </div>
      </Section>

      {/* Requirements & What's Not Accepted */}
      <Section k="etiquette.section.requirements-section" label="requirements" className="py-20 px-6 md:px-12 lg:px-24 bg-sand/30" data-testid="requirements-section">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Requirements */}
            <div>
              <h2 className="font-heading text-3xl text-charcoal mb-8 flex items-center gap-3">
                <Check className="w-6 h-6 text-jade-text" />
                <T k="etiquette.requirements">Requirements</T>
              </h2>
              <EditableList k="etiquette.requirements" items={requirements} newItem="New requirement" addLabel="Add requirement" className="space-y-4">
                {(req, index, Field) => (
                  <div className="flex items-start gap-4" data-testid={`requirement-${index}`}>
                    <div className="w-6 h-6 bg-jade-deep/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3 h-3 text-jade-text" />
                    </div>
                    <Field className="font-body text-stone-grey" />
                  </div>
                )}
              </EditableList>
            </div>

            {/* Not Accepted */}
            <div>
              <h2 className="font-heading text-3xl text-charcoal mb-8 flex items-center gap-3">
                <AlertCircle className="w-6 h-6 text-lipstick-red" />
                <T k="etiquette.not-accepted">Not Accepted</T>
              </h2>
              <EditableList k="etiquette.notaccepted" items={notAccepted} newItem="New item" addLabel="Add item" className="space-y-4">
                {(item, index, Field) => (
                  <div className="flex items-start gap-4" data-testid={`not-accepted-${index}`}>
                    <div className="w-6 h-6 bg-lipstick-red/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <AlertCircle className="w-3 h-3 text-lipstick-red" />
                    </div>
                    <Field className="font-body text-stone-grey" />
                  </div>
                )}
              </EditableList>
            </div>
          </div>
        </div>
      </Section>

      {/* Screening Info */}
      <Section k="etiquette.section.screening-section" label="screening" className="py-20 px-6 md:px-12 lg:px-24" data-testid="screening-section">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <T k="etiquette.safety-first" as="p" className="font-body text-sm tracking-widest uppercase text-champagne-gold mb-4">Safety First</T>
            <T k="etiquette.verification-process" as="h2" className="font-heading text-4xl text-charcoal mb-6">Verification Process</T>
          </div>

          <div className="glass rounded-xl p-8 md:p-12">
            <T k="etiquette.for-the-safety" as="p" multiline className="font-body text-stone-grey leading-relaxed mb-6">For the safety and comfort of both parties, all new acquaintances must complete a simple verification process. This can be accomplished through:</T>
            
            <ul className="space-y-4 mb-8">
              <li className="flex items-start gap-4">
                <span className="w-6 h-6 bg-champagne-gold text-white rounded-full flex items-center justify-center flex-shrink-0 text-sm font-medium">1</span>
                <div>
                  <T k="etiquette.provider-references" className="font-medium text-charcoal">Provider References:</T>
                  <T k="etiquette.two-recent-references" className="text-stone-grey">Two recent references from established companions (preferred method)</T>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <span className="w-6 h-6 bg-champagne-gold text-white rounded-full flex items-center justify-center flex-shrink-0 text-sm font-medium">2</span>
                <div>
                  <T k="etiquette.professional-verification" className="font-medium text-charcoal">Professional Verification:</T>
                  <T k="etiquette.linkedin-profile-or" className="text-stone-grey">LinkedIn profile or professional website with verifiable employment</T>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <span className="w-6 h-6 bg-champagne-gold text-white rounded-full flex items-center justify-center flex-shrink-0 text-sm font-medium">3</span>
                <div>
                  <T k="etiquette.review-sites" className="font-medium text-charcoal">Review Sites:</T>
                  <T k="etiquette.if-you-are" multiline className="text-stone-grey">If you are an active member of Private Delights, TNA Board, or P411, simply provide your username when submitting your inquiry. (Note: this option is for ACTIVE members, you must have at least TWO visible reviews with provider response/approval)</T>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <span className="w-6 h-6 bg-champagne-gold text-white rounded-full flex items-center justify-center flex-shrink-0 text-sm font-medium">4</span>
                <div>
                  <T k="etiquette.alternative-screening" className="font-medium text-charcoal">Alternative Screening:</T>
                  <T k="etiquette.for-those-without" className="text-stone-grey">For those without the above, alternative arrangements can be discussed</T>
                </div>
              </li>
            </ul>

            <T k="etiquette.all-information-is" as="p" multiline className="font-body text-sm text-taupe">All information is kept strictly confidential and is used solely for verification purposes. I never share client information with third parties.</T>
          </div>
        </div>
      </Section>

      {/* CTA */}
      <Section k="etiquette.section.etiquette-cta" label="etiquette cta" className="py-20 px-6 md:px-12 lg:px-24 bg-jade-deep" data-testid="etiquette-cta">
        <div className="max-w-4xl mx-auto text-center">
          <T k="etiquette.ready-to-connect" as="h2" className="font-heading text-4xl text-ivory mb-6">Ready to Connect?</T>
          <T k="etiquette.if-youve-read" as="p" multiline className="font-body text-ivory/80 mb-12 leading-relaxed">If you've read these guidelines and feel we might be a wonderful match, I invite you to submit an inquiry. I look forward to learning about you.</T>
          
          <Link to="/booking">
            <Button className="bg-champagne-gold text-charcoal hover:bg-champagne px-10 py-4 rounded-full font-body text-sm tracking-widest uppercase transition-all duration-300" data-testid="submit-inquiry-btn">
              <T k="etiquette.cta-btn">Submit Inquiry</T>
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </Section>
    </div>
  );
};

export default BookingEtiquette;
